import { Color } from "@repo/lib/color";
import {
  type QuillDelta,
  type QuillDeltaLineAttributes,
  QuillDeltaLineAttributesSchema,
  QuillDeltaOpSchema,
  QuillDeltaSchema,
  type QuillDeltaSegmentAttributes,
  QuillDeltaSegmentAttributesSchema,
} from "@repo/types";

export interface RichTextSegment {
  attributes?: QuillDeltaSegmentAttributes;
  content: string | { divider: true };
}

export interface RichTextLine {
  attributes: QuillDeltaLineAttributes;
  segments: RichTextSegment[];
}

const { min, max } = Math;

const segmentTypeKeys = QuillDeltaSegmentAttributesSchema.shape;
const lineTypeKeys = QuillDeltaLineAttributesSchema.shape;

const segmentAttrs = Object.keys(
  segmentTypeKeys
) as (keyof QuillDeltaSegmentAttributes)[];

const lineAttrs = Object.keys(
  lineTypeKeys
) as (keyof QuillDeltaLineAttributes)[];

export const filterDelta = (delta: QuillDelta) => {
  for (let i = 0; i < delta.ops.length; i++) {
    const op = delta.ops[i];

    if (op.attributes?.color) {
      op.attributes.color = Color.fromCSS(op.attributes.color).css();
    }
    if (op.attributes?.background) {
      op.attributes.background = Color.fromCSS(op.attributes.background).css();
    }
    if (op.attributes?.align) {
      if (!lineTypeKeys.align.safeParse(op.attributes.align).success) {
        delete op.attributes.align;
      }
    }
    if (op.attributes?.font) {
      if (!segmentTypeKeys.font.safeParse(op.attributes.font).success) {
        delete op.attributes.font;
      }
    }
    if (op.attributes?.header) {
      if (!lineTypeKeys.header.safeParse(op.attributes.header).success) {
        op.attributes.header = min(max(op.attributes.header | 0, 1), 3) as
          | 1
          | 2
          | 3;
      }
    }
    if (op.attributes?.indent) {
      if (!lineTypeKeys.indent.safeParse(op.attributes.indent).success) {
        op.attributes.indent = min(max(op.attributes.indent | 0, 1), 7) as
          | 1
          | 2
          | 3
          | 4
          | 5
          | 6
          | 7;
      }
    }
    if (op.attributes?.list) {
      if (!lineTypeKeys.list.safeParse(op.attributes.list).success) {
        op.attributes.list = "bullet";
      }
    }

    const parsed = QuillDeltaOpSchema.safeParse(op);
    if (parsed.success === false) {
      delta.ops.splice(i--, 1);
      console.warn("Failed to support delta op:", op, parsed.error);
      continue;
    }
    delta.ops[i] = parsed.data;
  }
  return delta;
};

export const convertDeltaToRichText = (delta: QuillDelta): RichTextLine[] => {
  delta = filterDelta(delta);

  const mismatch = QuillDeltaSchema.safeParse(delta);
  if (!mismatch.success) {
    console.error(
      "Data provided is corrupted: ",
      delta,
      mismatch.error.message
    );
    return [];
  }

  const richTextLines: RichTextLine[] = [];
  let currentLine: RichTextLine | undefined = undefined;

  for (let i = 0; i < delta.ops.length; i++) {
    const op = delta.ops[i];

    if (!currentLine) currentLine = { attributes: {}, segments: [] };

    if (typeof op.insert === "string") {
      const lines = op.insert.split("\n");
      const firstLine = lines.shift()!;

      // if firstLine isn't empty, create a segment for it
      const segment: RichTextSegment | null = firstLine
        ? { content: firstLine }
        : null;
      if (segment) currentLine.segments.push(segment);

      // apply attributes
      if (op.attributes) {
        // segment attributes
        // (only the first 'line' receives attributes)
        // (ops with segment attributes never include \n; so this is safe)
        if (segment) {
          segment.attributes = {};
          for (const attr of segmentAttrs) {
            if (op.attributes[attr]) {
              segment.attributes[attr] = op.attributes[attr] as never;
            }
          }
        }

        // line attributes
        // (line attributes only apply to line associated with the segment before the first \n)
        // (line attributes only ever appear for \n by itself as the insert; so this is safe)
        for (const attr of lineAttrs) {
          if (op.attributes[attr]) {
            currentLine.attributes[attr] = op.attributes[attr] as never;
          }
        }
      }

      // generate separate lines for each piece
      for (const line of lines) {
        richTextLines.push(currentLine);
        currentLine = { attributes: {}, segments: [] };
        if (line) currentLine.segments.push({ content: line });
      }
    } else if (op.insert?.divider) {
      if (currentLine.segments.length) richTextLines.push(currentLine);
      richTextLines.push({
        attributes: {},
        segments: [{ content: { divider: true } }],
      });
      currentLine = undefined;
    }
  }

  // ops should end with a final \n, but just in case, check for and add any contents after last \n
  if (currentLine?.segments.length) {
    richTextLines.push(currentLine);
  }

  return richTextLines;
};

// export const convertRichTextToDelta = (richText:RichTextLine[]): Delta => {}
